import React from "react";
import {
	ClientEntityType,
	GuarantorEntityType,
	OrganizationEntityType,
} from "@/lib/supabase/supabaseTypes";
import { PdfLetterhead } from "../pdf-template-components/pdf-letterhead.component";
import { PdfLayout } from "../pdf-template-components/pdf-layout.component";
import { PdfTemplateConfiguration } from "../../context/PdfTemplateConfigurationContext";
import "../fonts.css";
import { PdfRecipientAddress } from "../pdf-template-components/pdf-recipient-address.component";
import { PdfTitleAndDate } from "../pdf-template-components/pdf-title-and-date.component";
import { PaymentSlipDataInput } from "../pdf-template-components/qr-payment-slip/qr-payment-helpers";
import { QrPaymentSlip } from "../pdf-template-components/qr-payment-slip/qr-payment-slip.component";
import { calculateSammelrechnungRowsAndTotals } from "../../lib/utils/calculate";
import { PdfTemplateType } from "../document-template.types";
import { PdfTableTotalsSection } from "../pdf-template-components/pdf-table-totals-section.component";
import { PdfFooter } from "../pdf-template-components/pdf-footer.component";
import { PdfTaxAmountText } from "../pdf-template-components/pdf-tax-amount-text.component";
import { SammelrechnungenSortingOptions } from "../../types/enums";
import { getSammelrechnungTitle } from "./sammelrechnung/sammelrechnung.utils";
import { SammelrechnungTableRow } from "./sammelrechnung/sammelrechnung-table-row.component";

export interface SammelrechnungTemplateProps {
	recipient: ClientEntityType | GuarantorEntityType;
	client: ClientEntityType;
	sammelrechnungCalculationResult: ReturnType<
		typeof calculateSammelrechnungRowsAndTotals
	>;
	organization: OrganizationEntityType | null;
	pdfTemplateConfiguration: PdfTemplateConfiguration | undefined;
	paymentSlipData: PaymentSlipDataInput;
	currentDate: string;
	noPaymentSlip?: boolean;
	currencyConverterValues?: Record<string, string>;
}

export const SammelrechnungTemplate: React.FC<SammelrechnungTemplateProps> = ({
	recipient,
	client,
	organization,
	sammelrechnungCalculationResult,
	pdfTemplateConfiguration,
	paymentSlipData,
	currentDate,
	noPaymentSlip,
	currencyConverterValues,
}) => {
	if (
		!organization ||
		!recipient ||
		!pdfTemplateConfiguration ||
		!sammelrechnungCalculationResult ||
		!paymentSlipData ||
		!client
	) {
		return null;
	}
	const {
		rows: tableRows,
		startDate,
		endDate,
	} = sammelrechnungCalculationResult;

	// If all rows have the same client name, don't show client names
	const showClientNames = !tableRows.every(
		(row, _, arr) => row.clientName === arr[0].clientName
	);

	// If any row has "is_ignore_monthly_discount" set to true, show column
	// to mark these rows with a *
	const monthlyDiscountOnWorkAndFixedRate =
		sammelrechnungCalculationResult.monthlyDiscountOnWorkAndFixedRate.text;
	const monthlyDiscountOnSubtotal =
		sammelrechnungCalculationResult.monthlyDiscountOnSubtotal.text;
	const isMonthlyDiscountActive: boolean = Boolean(
		(monthlyDiscountOnWorkAndFixedRate &&
			monthlyDiscountOnWorkAndFixedRate !== "0.00") ||
			(monthlyDiscountOnSubtotal && monthlyDiscountOnSubtotal !== "0.00")
	);
	const someRowHasIgnoreMonthlyDiscount: boolean = Boolean(
		isMonthlyDiscountActive &&
			tableRows.some((row) => row.ignoreMonthlyDiscount)
	);

	const showPatientName = Boolean(client?.is_inv_w_pat_data ?? false);
	const showJobTitles = Boolean(client?.is_inv_w_job_title ?? false);

	let numberOfColumns = 7;
	let numberOfAdditionalColumns = 1;
	const jobCodeWidth = 13;
	const dateWidth = 13;
	const totalColumnWidth = 12;
	let jobTitleColumnWidth = 19.5;
	let patientNameColumnWidth = 19.5;
	let clientNameColumnWidth = 19.5;
	let ignoreMonthlyDiscountColumnWidth = 1.5;
	const extraSpace = 2;
	let spaceToDistribute = 0;

	if (!someRowHasIgnoreMonthlyDiscount) {
		ignoreMonthlyDiscountColumnWidth = 0;
		jobTitleColumnWidth += 0.5;
		patientNameColumnWidth += 0.5;
		clientNameColumnWidth += 0.5;
		numberOfAdditionalColumns -= 1;
	}

	if (!showPatientName) {
		numberOfColumns -= 1;
		spaceToDistribute += patientNameColumnWidth;
	}
	if (!showClientNames) {
		numberOfColumns -= 1;
		spaceToDistribute += clientNameColumnWidth;
	}
	if (!showJobTitles) {
		numberOfColumns -= 1;
		spaceToDistribute += jobTitleColumnWidth;
	}

	if (spaceToDistribute > 0) {
		if (showJobTitles) {
			jobTitleColumnWidth += spaceToDistribute / 2;
			spaceToDistribute -= spaceToDistribute / 2;
		}
		if (showClientNames) {
			clientNameColumnWidth += spaceToDistribute / 2;
			spaceToDistribute -= spaceToDistribute / 2;
		}
		if (showPatientName) {
			patientNameColumnWidth += spaceToDistribute;
		}
	}

	// Historically, patient last name was the default sorting option so we use it as fallback
	const sortingOption =
		client?.srg_sorting_option ??
		(showPatientName
			? SammelrechnungenSortingOptions.PATIENT_LAST_NAME
			: SammelrechnungenSortingOptions.JOB_CODE);

	// Determine VAT display preference: Client override → PDF config → Default (exclusive)
	const templateConfig =
		pdfTemplateConfiguration[PdfTemplateType.SAMMELRECHNUNG];
	let showZazInclusiveAmounts: boolean;
	if (
		client?.vat_display_override !== null &&
		client?.vat_display_override !== undefined
	) {
		// Use client override: true = inclusive, false = exclusive
		showZazInclusiveAmounts =
			client.vat_display_override == "inclusive" ? true : false;
	} else {
		// Fall back to lab standard (PDF config)
		showZazInclusiveAmounts =
			"showZazInclusiveAmounts" in templateConfig
				? Boolean(templateConfig.showZazInclusiveAmounts)
				: false;
	}

	// Determine the VAT disclaimer text
	const vatDisclaimer = showZazInclusiveAmounts
		? "inkl. MwSt."
		: "exkl. MwSt.";
	// Check if practitioner grouping is enabled
	const groupByPractitioner = Boolean(
		client?.is_srg_grouped_by_prac ?? false
	);

	// Get bold configuration from general settings (only used for practitioner headers, not table rows)
	const isPractitionerBold =
		pdfTemplateConfiguration.general.isPractitionerBold ?? false;

	const sortedRows = tableRows.sort((a, b) => {
		if (sortingOption === SammelrechnungenSortingOptions.JOB_CODE) {
			return a.jobCode.localeCompare(b.jobCode);
		} else if (sortingOption === SammelrechnungenSortingOptions.JOB_TITLE) {
			const titleCompare = a.jobTitle.localeCompare(b.jobTitle);
			return titleCompare !== 0
				? titleCompare
				: a.jobCode.localeCompare(b.jobCode);
		} else if (
			sortingOption === SammelrechnungenSortingOptions.PATIENT_LAST_NAME
		) {
			return a.patientName.lastName.localeCompare(b.patientName.lastName);
		} else if (
			sortingOption === SammelrechnungenSortingOptions.PATIENT_CODE
		) {
			return a.patientName.code.localeCompare(b.patientName.code);
		} else {
			return a.jobCode.localeCompare(b.jobCode);
		}
	});

	const fontSize = {
		sm: "6pt",
		md: "7pt",
		lg: "9pt",
	};

	return (
		<PdfLayout pdfTemplateConfiguration={pdfTemplateConfiguration}>
			<PdfLetterhead
				organization={organization}
				pdfTemplateConfiguration={pdfTemplateConfiguration}
				pdfType={PdfTemplateType.SAMMELRECHNUNG}
				mwstNumber={sammelrechnungCalculationResult.jobMwstNumber ?? ""}
			/>
			<PdfRecipientAddress
				recipient={recipient}
				pdfTemplateConfiguration={pdfTemplateConfiguration}
				template={PdfTemplateType.SAMMELRECHNUNG}
			/>
			<div
				aria-label="Dokumenteninhalt"
				style={{
					paddingTop: "5mm",
					paddingLeft: "5mm",
					paddingRight: "10mm",
				}}
			>
				<PdfTitleAndDate
					generalConfig={pdfTemplateConfiguration.general}
					title={getSammelrechnungTitle(
						startDate,
						endDate,
						currentDate,
						templateConfig.titleDateMode
					)}
					date={currentDate}
				/>
				<div
					aria-label="Zusatzinformationen"
					style={{
						paddingTop: "5mm",
						fontSize: "10pt",
					}}
				>
					<table>
						<thead>
							<tr>
								<td
									style={{
										paddingRight: "5mm",
									}}
								>
									Rg.-Nummer
								</td>
								<td>{paymentSlipData.invoiceNumber}</td>
							</tr>
						</thead>
					</table>
				</div>
				<div
					aria-label="Tabelle mit allen Aufträgen"
					style={{
						paddingTop: "5mm",
						fontSize: "10pt",
					}}
				>
					<table
						style={{
							width: "100%",
							borderCollapse: "collapse",
							fontSize:
								fontSize[
									pdfTemplateConfiguration.general.size ??
										"lg"
								],
							backgroundColor: "white",
						}}
					>
						<thead>
							<tr
								style={{
									backgroundColor:
										pdfTemplateConfiguration.general
											.tableBackgroundColor ?? "#d3d3d3",
									borderBottom: "1px solid black",
								}}
							>
								<th
									style={{
										textAlign: "left",
										width: `${jobCodeWidth}%`,
									}}
								>
									Auftrag
								</th>
								<th
									style={{
										textAlign: "left",
										width: `${dateWidth}%`,
									}}
								>
									Datum
								</th>
								{showClientNames && (
									<th
										style={{
											textAlign: "left",
											width: `${clientNameColumnWidth}%`,
										}}
									>
										Auftraggeber
									</th>
								)}
								{showPatientName && (
									<th
										style={{
											textAlign: "left",
											width: `${patientNameColumnWidth}%`,
										}}
									>
										Patient
									</th>
								)}
								{showJobTitles && (
									<th
										style={{
											textAlign: "left",
											width: `${jobTitleColumnWidth}%`,
										}}
									>
										Titel
									</th>
								)}
								{extraSpace > 0 && (
									<td style={{ width: `${extraSpace}mm` }} />
								)}
								<th
									style={{
										textAlign: "right",
										width: `${totalColumnWidth}%`,
									}}
								>
									Betrag
								</th>
								{someRowHasIgnoreMonthlyDiscount && (
									<th
										style={{
											textAlign: "right",
											width: `${ignoreMonthlyDiscountColumnWidth}%`,
										}}
									></th>
								)}
							</tr>
						</thead>
						<tbody>
							{groupByPractitioner
								? (() => {
										// Group rows by practitioner
										const practitionerGroups =
											sortedRows.reduce(
												(acc, row) => {
													const practitionerKey =
														row.practitionerName.id?.toString() ??
														"no-id";
													if (!acc[practitionerKey]) {
														acc[practitionerKey] = {
															practitioner:
																row.practitionerName,
															rows: [],
															subjectToTax: 0,
															exemptFromTax: 0,
															total: 0,
														};
													}
													acc[
														practitionerKey
													].rows.push(row);
													// Use inclusive or exclusive amounts based on VAT display preference
													acc[
														practitionerKey
													].subjectToTax +=
														showZazInclusiveAmounts
															? row.jobTotals
																	.subjectToDrTaxAmountInclVat
																	.value
															: row.jobTotals
																	.subjectToDrTaxAmountExclVat
																	.value;
													acc[
														practitionerKey
													].exemptFromTax +=
														showZazInclusiveAmounts
															? row.jobTotals
																	.exemptFromTaxAmountInclVat
																	.value
															: row.jobTotals
																	.exemptFromTaxAmountExclVat
																	.value;
													acc[
														practitionerKey
													].total +=
														row.jobTotals.total.value;
													return acc;
												},
												{} as Record<
													string,
													{
														practitioner: (typeof sortedRows)[0]["practitionerName"];
														rows: typeof sortedRows;
														subjectToTax: number;
														exemptFromTax: number;
														total: number;
													}
												>
											);

										return (
											<>
												{Object.values(
													practitionerGroups
												).map((group, groupIndex) => (
													<React.Fragment
														key={`practitioner-group-${
															group.practitioner
																.id || "none"
														}-${groupIndex}`}
													>
														<tr
															key={`practitioner-${
																group
																	.practitioner
																	.id ||
																"none"
															}-${groupIndex}`}
														>
															<td
																colSpan={
																	numberOfColumns +
																	numberOfAdditionalColumns
																}
																style={{
																	textAlign:
																		"left",
																	fontWeight:
																		isPractitionerBold
																			? "bold"
																			: "normal",
																	paddingTop:
																		"2px",
																}}
															>
																{group
																	.practitioner
																	.firstName ||
																group
																	.practitioner
																	.lastName
																	? `${
																			group
																				.practitioner
																				.title
																				? group
																						.practitioner
																						.title +
																					" "
																				: ""
																		}${
																			group
																				.practitioner
																				.firstName ||
																			""
																		} ${
																			group
																				.practitioner
																				.lastName ||
																			""
																		}`.trim()
																	: "Ohne Behandler"}
															</td>
														</tr>
														{group.rows.map(
															(row, i) => (
																<SammelrechnungTableRow
																	key={`${
																		group
																			.practitioner
																			.id ||
																		"none"
																	}-${
																		row.jobCode
																	}-${i}`}
																	row={row}
																	index={i}
																	showClientNames={
																		showClientNames
																	}
																	showPatientName={
																		showPatientName
																	}
																	showJobTitles={
																		showJobTitles
																	}
																	extraSpace={
																		extraSpace
																	}
																	someRowHasIgnoreMonthlyDiscount={
																		someRowHasIgnoreMonthlyDiscount
																	}
																	ignoreMonthlyDiscountColumnWidth={
																		ignoreMonthlyDiscountColumnWidth
																	}
																	recipient={
																		client as ClientEntityType
																	}
																	keyPrefix={`${
																		group
																			.practitioner
																			.id ||
																		"none"
																	}-`}
																/>
															)
														)}
														<tr
															key={`practitioner-total-${group.practitioner.id}`}
														>
															<td></td>
															<td
																colSpan={2}
																style={{
																	textAlign:
																		"left",
																	fontStyle:
																		"italic",
																}}
															>
																Steuerpfl.
																Betrag (
																{vatDisclaimer}
																):{" "}
																{group.subjectToTax.toFixed(
																	2
																)}{" "}
																{
																	paymentSlipData.currency
																}
															</td>
															{showClientNames && (
																<td></td>
															)}
															{showJobTitles && (
																<td></td>
															)}
															{extraSpace > 0 && (
																<td></td>
															)}
															<td
																style={{
																	textAlign:
																		"right",
																	fontStyle:
																		"italic",
																}}
															>
																{group.total.toFixed(
																	2
																)}
															</td>
															{someRowHasIgnoreMonthlyDiscount && (
																<td></td>
															)}
														</tr>
													</React.Fragment>
												))}
											</>
										);
									})()
								: /* Regular sorting - no grouping */
									sortedRows.map((row, i) => (
										<SammelrechnungTableRow
											key={row.jobCode + "-" + i}
											row={row}
											index={i}
											showClientNames={showClientNames}
											showPatientName={showPatientName}
											showJobTitles={showJobTitles}
											extraSpace={extraSpace}
											someRowHasIgnoreMonthlyDiscount={
												someRowHasIgnoreMonthlyDiscount
											}
											ignoreMonthlyDiscountColumnWidth={
												ignoreMonthlyDiscountColumnWidth
											}
											recipient={
												client as ClientEntityType
											}
										/>
									))}
							<PdfTableTotalsSection
								totals={{ ...sammelrechnungCalculationResult }}
								numberOfColumns={numberOfColumns}
								numberOfAdditionalColumns={
									numberOfAdditionalColumns
								}
								pdfTemplateConfiguration={
									pdfTemplateConfiguration
								}
								organization={organization}
								monthlyDiscountOnWorkAndFixedRate={
									sammelrechnungCalculationResult
										.monthlyDiscountOnWorkAndFixedRate.text
								}
								monthlyDiscountOnSubtotal={
									sammelrechnungCalculationResult
										.monthlyDiscountOnSubtotal.text
								}
								client={client}
								isMonthlyInvoice
								isMwstEust={
									client.is_mwst_eust ??
									organization.is_mwst_eust ??
									false
								}
								templateType={PdfTemplateType.SAMMELRECHNUNG}
								currency={paymentSlipData.currency}
							/>
						</tbody>
					</table>
				</div>
				<PdfTaxAmountText
					templateType={PdfTemplateType.SAMMELRECHNUNG}
					pdfTemplateConfiguration={pdfTemplateConfiguration}
					jobTotals={sammelrechnungCalculationResult}
					generalConfig={pdfTemplateConfiguration.general}
					client={client}
					currency={paymentSlipData.currency}
				/>
				{someRowHasIgnoreMonthlyDiscount && (
					<div
						aria-label="Auftrag mit Monatsrabatt"
						style={{
							paddingTop: "5mm",
							fontSize: "10pt",
						}}
					>
						* Auf diesen Auftrag wurde kein Monatsrabatt angewandt.
					</div>
				)}
				<PdfFooter
					templateType={PdfTemplateType.SAMMELRECHNUNG}
					pdfTemplateConfiguration={pdfTemplateConfiguration}
					client={client}
					currencyConverterValues={currencyConverterValues}
				/>
				{!noPaymentSlip && (
					<QrPaymentSlip
						pdfTemplateConfiguration={pdfTemplateConfiguration}
						paymentSlipData={paymentSlipData}
						textAbove={
							pdfTemplateConfiguration[
								PdfTemplateType.SAMMELRECHNUNG
							].textAbovePaymentSlip
						}
					/>
				)}
			</div>
		</PdfLayout>
	);
};
